import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiClient, setAdminToken, formatApiError } from "@/lib/api";
import { Logo } from "@/components/landing/Logo";

export const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await apiClient.post("/admin/login", { email, password });
      setAdminToken(data.token);
      toast.success("Welcome back, admin.");
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(formatApiError(err, "Login failed. Check your credentials."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-white px-5" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.33, 1, 0.68, 1] }}
        className="w-full max-w-md"
      >
        <div className="mb-10 flex justify-center">
          <Link to="/" data-testid="admin-login-logo"><Logo /></Link>
        </div>
        <form onSubmit={submit} className="luxury-card rounded-xl p-8 sm:p-10" data-testid="admin-login-form">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/5">
              <Lock size={18} className="text-[#A07E1F]" />
            </div>
            <h1 className="font-serif-display text-3xl font-semibold text-[#12294D]">Admin Portal</h1>
            <p className="mt-2 text-sm text-[#5E6E85]">Manage samples, blog, reviews, rates & enquiries.</p>
          </div>
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="admin-email" className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">Email</Label>
              <Input id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" className="border-[#D4AF37]/25 bg-white text-[#12294D]" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password" className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">Password</Label>
              <Input id="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" className="border-[#D4AF37]/25 bg-white text-[#12294D]" />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-[#D4AF37] py-4 font-mono-accent text-xs font-bold uppercase tracking-[0.2em] text-[#0A1E3C] transition-colors duration-300 hover:bg-[#C9A227] disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />}
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
        <p className="mt-6 text-center">
          <Link to="/" data-testid="admin-back-home" className="text-xs text-[#75849B] transition-colors hover:text-[#A07E1F]">← Back to website</Link>
        </p>
      </motion.div>
    </div>
  );
};
